import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/axios';

// ── Sparkline (kopiert aus CockpitDashboard) ────────────────────────────
function Sparkline({ points, color = '#0F766E', invert = false, height = 40, width = 160 }) {
  if (!points || points.length < 2) {
    return <div className="text-[11px] text-gray-300">Nicht genug Daten</div>;
  }
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const step = width / (points.length - 1);
  const d = points
    .map((p, i) => {
      const x = i * step;
      const yRaw = (p - min) / range;
      const y = invert ? yRaw * height : height - yRaw * height;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-10">
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function KpiCard({ label, value, sub, accent }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-4">
      <div className="text-[10.5px] font-semibold tracking-wider text-gray-400 uppercase">{label}</div>
      <div className="text-[26px] font-bold mt-1.5" style={{ color: accent || '#0F766E' }}>
        {value ?? '—'}
      </div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
      <h3 className="text-[14px] font-semibold text-gray-900 flex items-center gap-2 mb-3">
        <span className="text-teal text-[15px]">{icon}</span>
        {title}
      </h3>
      {children}
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="text-[12px] text-gray-400 py-4 text-center">{text}</div>;
}

// ── Passwort-Gate ───────────────────────────────────────────────────────
function PasswordGate({ projectName, onUnlock, error }) {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await onUnlock(password);
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-6">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-black/[.07] p-8 w-full max-w-[400px]">
        <div
          className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center text-[22px] mb-4"
          style={{ backgroundColor: '#E6F4F2', color: '#0F766E' }}
        >
          ▦
        </div>
        <h1 className="text-[18px] font-bold text-center text-gray-900 mb-1">Kunden Cockpit</h1>
        {projectName && (
          <div className="text-[12.5px] text-center text-gray-500 mb-5">{projectName}</div>
        )}
        <label className="text-[12px] text-gray-600 block mb-1.5 font-medium">Passwort</label>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal mb-3"
        />
        {error && (
          <div className="text-[11.5px] text-red-700 bg-red-50 rounded-md px-3 py-2 mb-3">{error}</div>
        )}
        <button
          type="submit"
          disabled={submitting || !password}
          className="w-full bg-teal text-white rounded-lg py-2.5 text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? 'Prüfe…' : 'Entsperren'}
        </button>
      </form>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
export default function ShareCockpit() {
  const { token } = useParams();
  const [state, setState] = useState({
    loading: true,
    requiresPassword: false,
    data: null,
    projectName: null,
    error: null,
  });

  const fetchInitial = async () => {
    try {
      const { data } = await api.get(`/share/cockpit/${token}`, { skipProject: true });
      if (data?.requires_password) {
        setState({ loading: false, requiresPassword: true, data: null, projectName: data.project_name, error: null });
      } else {
        setState({ loading: false, requiresPassword: false, data, projectName: data?.project?.name, error: null });
      }
    } catch (e) {
      setState({
        loading: false,
        requiresPassword: false,
        data: null,
        projectName: null,
        error: e.response?.data?.message || 'Share-Link ungültig oder nicht mehr aktiv.',
      });
    }
  };

  useEffect(() => {
    fetchInitial();
    // eslint-disable-next-line
  }, [token]);

  const unlock = async (password) => {
    try {
      const { data } = await api.post(`/share/cockpit/${token}/auth`, { password }, { skipProject: true });
      setState((s) => ({ ...s, requiresPassword: false, data, error: null }));
    } catch (e) {
      setState((s) => ({ ...s, error: e.response?.data?.message || 'Falsches Passwort.' }));
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-[13px] text-gray-500">Lade Cockpit…</div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-black/[.07] p-8 max-w-[400px] text-center">
          <div className="text-[40px] mb-3">⚠</div>
          <h1 className="text-[17px] font-bold text-gray-900 mb-1">Link nicht verfügbar</h1>
          <p className="text-[12.5px] text-gray-500">{state.error}</p>
        </div>
      </div>
    );
  }

  if (state.requiresPassword) {
    return <PasswordGate projectName={state.projectName} onUnlock={unlock} error={state.error} />;
  }

  return <CockpitPublicView data={state.data} />;
}

// ── Public Cockpit View ─────────────────────────────────────────────────
function CockpitPublicView({ data }) {
  const analyses = data?.analyses || [];
  const keywords = data?.keywords || [];
  const measures = data?.measures || [];
  const reports = data?.reports || [];
  const project = data?.project || {};
  const title = data?.title || 'Kunden Cockpit';

  const kpis = useMemo(() => {
    const doneAnalyses = analyses.filter((a) => typeof a.score === 'number');
    const avgScore = doneAnalyses.length
      ? Math.round(doneAnalyses.reduce((s, a) => s + a.score, 0) / doneAnalyses.length)
      : null;
    const rankedKw = keywords
      .filter((k) => k.position_current != null && Number(k.position_current) > 0)
      .sort((a, b) => Number(a.position_current) - Number(b.position_current));
    const topKw = rankedKw[0];
    const openMeasures = measures.filter((m) => m.status === 'open' || m.status === 'in_progress' || m.status === 'pending');
    return {
      avgScore,
      analysesCount: doneAnalyses.length,
      topKw,
      openMeasures: openMeasures.length,
      reportsCount: reports.length,
    };
  }, [analyses, keywords, measures, reports]);

  const rankingsTrend = useMemo(() => {
    const byDate = {};
    keywords.forEach((k) => {
      (k.history || []).forEach((h) => {
        if (!byDate[h.date]) byDate[h.date] = { sum: 0, n: 0 };
        byDate[h.date].sum += Number(h.position) || 0;
        byDate[h.date].n += 1;
      });
    });
    return Object.keys(byDate).sort().slice(-14).map((d) => byDate[d].sum / byDate[d].n);
  }, [keywords]);

  const scoreTrend = useMemo(
    () => analyses.filter((a) => typeof a.score === 'number').slice(-10).map((a) => a.score),
    [analyses]
  );

  return (
    <div className="min-h-screen bg-bg">
      {/* Header */}
      <header className="bg-white border-b border-black/[.07]">
        <div className="max-w-[1160px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-[13px]"
              style={{ backgroundColor: project.color || '#0F766E' }}
            >
              {(project.name || '?')[0]}
            </div>
            <div>
              <div className="text-[13px] font-semibold text-gray-900">{title}</div>
              <div className="text-[11px] text-gray-500">
                {project.name}
                {project.domain ? ` · ${project.domain}` : ''}
              </div>
            </div>
          </div>
          <div className="text-[10.5px] text-gray-400">
            Stand {new Date(data.generated_at).toLocaleString('de')}
          </div>
        </div>
      </header>

      <main className="max-w-[1160px] mx-auto px-6 py-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <KpiCard
            label="Ø Analyse-Score"
            value={kpis.avgScore != null ? kpis.avgScore : '—'}
            sub={kpis.analysesCount ? `${kpis.analysesCount} Analysen` : 'Noch keine Analysen'}
            accent="#0C9488"
          />
          <KpiCard
            label="Top-Position"
            value={kpis.topKw ? `#${Math.round(Number(kpis.topKw.position_current))}` : '—'}
            sub={kpis.topKw ? kpis.topKw.keyword : 'Noch keine Rankings'}
            accent="#1C2E3C"
          />
          <KpiCard
            label="Offene Maßnahmen"
            value={kpis.openMeasures}
            sub="SEO-Backlog"
            accent="#F59E0B"
          />
          <KpiCard
            label="Monatsreports"
            value={kpis.reportsCount}
            sub="Generiert"
            accent="#6D28D9"
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[12.5px] font-semibold text-gray-700">Rankings-Trend</div>
              <div className="text-[10.5px] text-gray-400">Ø Position, letzte 14 Messungen</div>
            </div>
            <Sparkline points={rankingsTrend} color="#1C2E3C" invert />
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[12.5px] font-semibold text-gray-700">CRO-Score-Verlauf</div>
              <div className="text-[10.5px] text-gray-400">letzte 10 Analysen</div>
            </div>
            <Sparkline points={scoreTrend} color="#0C9488" />
          </div>
        </div>

        {/* Module */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Section title="Letzte CRO-Analysen" icon="⟳">
            {analyses.length === 0 ? (
              <EmptyState text="Noch keine Analysen." />
            ) : (
              <div className="divide-y divide-gray-100">
                {analyses.slice(0, 5).map((a) => (
                  <div key={a.id} className="py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-medium text-gray-900 truncate">{a.url}</div>
                      <div className="text-[10.5px] text-gray-400">
                        {a.page_type} · {new Date(a.created_at).toLocaleDateString('de')}
                      </div>
                    </div>
                    <div className="text-[14px] font-bold text-teal">{a.score ?? '—'}</div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Top-Rankings" icon="◉">
            {keywords.length === 0 ? (
              <EmptyState text="Keine Keywords." />
            ) : (
              <div className="divide-y divide-gray-100">
                {keywords
                  .filter((k) => k.position_current != null)
                  .sort((a, b) => Number(a.position_current) - Number(b.position_current))
                  .slice(0, 5)
                  .map((k) => (
                    <div key={k.id} className="py-2.5 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-[12.5px] font-medium text-gray-900 truncate">{k.keyword}</div>
                        <div className="text-[10.5px] text-gray-400">
                          {k.impressions != null ? `${k.impressions} Impressionen` : k.category}
                        </div>
                      </div>
                      <div className="text-[13.5px] font-bold text-sidebar">
                        #{Math.round(Number(k.position_current))}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </Section>

          <Section title="Offene Maßnahmen" icon="☑">
            {measures.length === 0 ? (
              <EmptyState text="Keine Maßnahmen im Backlog." />
            ) : (
              <div className="divide-y divide-gray-100">
                {measures
                  .filter((m) => m.status !== 'done' && m.status !== 'completed')
                  .slice(0, 5)
                  .map((m) => (
                    <div key={m.id} className="py-2.5">
                      <div className="text-[12.5px] font-medium text-gray-900">{m.title || m.name}</div>
                      <div className="text-[10.5px] text-gray-400 mt-0.5">
                        {m.category} · {m.status}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </Section>

          <Section title="Monatsreports" icon="▦">
            {reports.length === 0 ? (
              <EmptyState text="Keine Reports generiert." />
            ) : (
              <div className="divide-y divide-gray-100">
                {reports.slice(0, 5).map((r) => {
                  const periodLabel = r.period_start
                    ? new Date(r.period_start).toLocaleDateString('de', { month: 'short', year: 'numeric' })
                    : null;
                  return (
                    <div key={r.id} className="py-2.5 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-[12.5px] font-medium text-gray-900 truncate">
                          {r.title || periodLabel || `Report #${r.id}`}
                        </div>
                        <div className="text-[10.5px] text-gray-400">
                          {r.created_at ? new Date(r.created_at).toLocaleDateString('de') : '—'}
                        </div>
                      </div>
                      <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                        {r.status || 'draft'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </div>

        <div className="text-[10.5px] text-gray-400 text-center mt-6">
          Powered by KonversionOptimus AI · plattform.konversion.digital
        </div>
      </main>
    </div>
  );
}
